import React, { useState } from "react";
import { Grid, ListItem, ListItemText, Typography, Box, Menu, MenuItem, ListItemIcon, ListItemButton, CircularProgress, useTheme } from "@mui/material";
import Barcode_Mini_Icon from '../../../../Assets/Barcode_Mini_Icon.png'
import Barcode_Mini_Icon_White from '../../../../Assets/Barcode_Mini_Icon_White.png'
import { Launch, MoreHoriz, CloudUpload } from "@mui/icons-material";
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import FlagIcon from '@mui/icons-material/Flag';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useFeatureSupported } from '../../../../FeatureFlagsContext';
import ReportScanDialog from './ReportScanDialog';

export default function BarcodeCenterControl(props: any) {
    const { cargoResponse, handleReupload, handleRexport, handleReportScan, setShowAuditDims, selectedScanIndex } = props;
    const [reuploadLoading, setReuploadLoading] = useState(false);
    const [rexportLoading, setRexportLoading] = useState(false);
    const [reportScanOpen, setReportScanOpen] = useState(false);
    const { t } = useTranslation();
    const theme = useTheme();
    const recallSupported = useFeatureSupported ? useFeatureSupported('Recall') : false;
    const barcodeIcon = theme.palette.mode === 'dark' ? Barcode_Mini_Icon_White : Barcode_Mini_Icon;

    const styles = {
        barcodeField: {
            mt: 2,
            mb: 2
        },
        barcodeImage: {
            display: "inline",
            pr: 1,
            pl: 0.5
        },
        awaitingScanText: {
            color: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : theme.palette.text.primary,
            fontWeight: 600,
            fontSize: '0.95rem',
        },
        moreIcon: {
            color: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : theme.palette.text.primary,
            fontSize: 15,
        },
        detailsButton: {
            color: "#FFFFFF",
            backgroundColor: 'var(--spectre-navigation)',
            '&:hover': {
                backgroundColor: 'var(--spectre-navigation-hover)',
                borderColor: 'var(--spectre-border)'
            },
            '&:disabled': {
                color: "#FFFFFF",
                backgroundColor: 'var(--spectre-surface-muted)'
            },
            '&.Mui-disabled': {
                color: "#FFFFFF",
                backgroundColor: 'var(--spectre-surface-muted)'
            },
        },
        menuPaper: {
            boxShadow: '0px 4px 16px rgba(0,0,0,0.15)',
            borderRadius: 2,
            minWidth: 180,
            p: 0.5,
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            px: 2,
        },
    }

    // Menu state
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Async wrappers for button actions
    const handleReuploadClick = async () => {
        if (!cargoResponse || !handleReupload) return;
        setReuploadLoading(true);
        try {
            await handleReupload(cargoResponse);
        } finally {
            setReuploadLoading(false);
        }
    };
    const handleRexportClick = async () => {
        if (!cargoResponse || !handleRexport) return;
        setRexportLoading(true);
        try {
            await handleRexport(cargoResponse);
        } finally {
            setRexportLoading(false);
        }
    };

    return (
        <Grid item md={12} display={{ xs: 'none', md: 'block' }} sx={styles.barcodeField}>
            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
            >
                <Grid item md={10} xs={12} >
                    <ListItem sx={{ backgroundColor: 'var(--spectre-surface)', }}>
                        {cargoResponse ?
                            <ListItemText>
                                <Box
                                    component="img"
                                    sx={styles.barcodeImage}
                                    alt="Barcode:"
                                    src={barcodeIcon}
                                />:&nbsp;
                                <Typography display="inline" sx={{ fontWeight: 500, wordWrap: "break-word" }}>{cargoResponse ? cargoResponse.barcode : ""}</Typography>
                            </ListItemText>
                            :
                            <ListItemText style={{ display: 'flex', justifyContent: 'center' }}>
                                <Typography display="inline" sx={styles.awaitingScanText}>{t('control.centerControl.awaitingScan')}</Typography>
                            </ListItemText>
                        }
                    </ListItem>
                </Grid>
                <Grid item md={1}>
                    <ListItemButton onClick={handleMenuOpen} aria-label="more options"
                        sx={{
                            backgroundColor: 'var(--spectre-surface)',
                            borderLeft: '1px solid',
                            borderColor: 'var(--spectre-border)'
                        }}
                    >
                        <ListItemText
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <MoreHoriz sx={styles.moreIcon} />
                        </ListItemText>
                    </ListItemButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        transitionDuration={0}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{ sx: styles.menuPaper }}
                        MenuListProps={{ sx: { p: 0 } }}
                    >
                        <MenuItem
                            sx={styles.menuItem}
                            component="a"
                            href={`https://spectre-licensing.com/Cargo/Details/${cargoResponse ? cargoResponse.cargoID : ""}`}
                            target="_blank"
                            onClick={handleMenuClose}
                            disabled={!(cargoResponse && _.get(cargoResponse, 'cargoID', undefined))}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>{<Launch sx={{ fontSize: 18 }} />}</ListItemIcon>
                            <span>{t('control.centerControl.centerBarcodeFieldButtons.viewDetails') || 'View Details'}</span>
                        </MenuItem>
                        {recallSupported && [
                            <MenuItem 
                                key="reupload"
                                sx={styles.menuItem} 
                                onClick={async () => { await handleReuploadClick(); }} 
                                disabled={!cargoResponse || reuploadLoading || cargoResponse.uploadCode === "0"}
                            > 
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    {reuploadLoading ? <CircularProgress size={18} /> : <CloudUpload sx={{ fontSize: 18 }} />}
                                </ListItemIcon>
                                <span>{t('control.centerControl.centerBarcodeFieldButtons.reupload') || 'Re-upload'}</span>
                            </MenuItem>,
                            <MenuItem 
                                key="reexport"
                                sx={styles.menuItem} 
                                onClick={async () => { await handleRexportClick(); }} 
                                disabled={!(cargoResponse && _.get(cargoResponse, 'cargoID', undefined)) || rexportLoading}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    {rexportLoading ? <CircularProgress size={18} /> : <DriveFileMoveIcon sx={{ fontSize: 18 }} />}
                                </ListItemIcon>
                                <span>{t('control.centerControl.centerBarcodeFieldButtons.reexport') || 'Re-export'}</span>
                            </MenuItem>,
                            <MenuItem
                                key="reportScan"
                                sx={styles.menuItem}
                                onClick={() => { handleMenuClose(); setReportScanOpen(true); }}
                                disabled={!cargoResponse?.cargoFolder}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <FlagIcon sx={{ fontSize: 18 }} />
                                </ListItemIcon>
                                <span>{t('control.centerControl.centerBarcodeFieldButtons.reportScan') || 'Report Scan'}</span>
                            </MenuItem>
                        ]}
                        {!recallSupported && (
                            <MenuItem
                                sx={styles.menuItem}
                                disabled={selectedScanIndex === 0 && cargoResponse ? false : true}
                                onClick={() => { setShowAuditDims(true); handleMenuClose(); }}
                            >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <FlagIcon sx={{ fontSize: 18 }} />
                                </ListItemIcon>
                                <span>{t('control.centerControl.centerBarcodeFieldButtons.reportScan') || 'Report Scan'}</span>
                            </MenuItem>
                        )}

                    </Menu>
                </Grid>
            </Grid>
            <ReportScanDialog
                open={reportScanOpen}
                onClose={() => setReportScanOpen(false)}
                cargoResponse={cargoResponse}
                handleReportScan={handleReportScan}
            />
        </Grid>
    )
}